import React from 'react';

const toLower = (value) => (typeof value === 'string' ? value.toLowerCase() : '');
const numberOrZero = (value) => parseFloat(value ?? 0) || 0;
const esCuotaConvenio = (cuota) => toLower(cuota.observaciones).includes('convenio');
const esCuotaPagada = (cuota) => ['pagado', 'prepagado'].includes(cuota.estado);

const getEstadoBadge = (estado) => {
  const estados = {
    vencido: { label: 'Vencido', className: 'bg-red-100 text-red-800' },
    vence_hoy: { label: 'Vence Hoy', className: 'bg-orange-100 text-orange-800' },
    pagado: { label: 'Pagado', className: 'bg-green-100 text-green-800' },
    prepagado: { label: 'Prepagado', className: 'bg-blue-100 text-blue-800' },
    default: { label: 'Pendiente', className: 'bg-yellow-100 text-yellow-800' }
  };

  return estados[estado] || estados.default;
};

const prioridadCuota = (cuota) => {
  if (esCuotaConvenio(cuota)) return 2;
  if (esCuotaPagada(cuota)) return 0;
  return 1;
};

const ordenarCuotas = (cuotas = []) =>
  [...cuotas].sort((a, b) => {
    const priorityDiff = prioridadCuota(a) - prioridadCuota(b);
    if (priorityDiff !== 0) return priorityDiff;

    const numeroDiff = (Number(a.numero_cuota) || 0) - (Number(b.numero_cuota) || 0);
    if (numeroDiff !== 0) return numeroDiff;

    return (
      (new Date(a.fecha_vencimiento || 0).getTime() || 0) -
      (new Date(b.fecha_vencimiento || 0).getTime() || 0)
    );
  });

const formatCurrency = (value) => `S/ ${(Number(value) || 0).toFixed(2)}`;

const calcularSaldoPendiente = (cuota) => {
  const montoAPagar = parseFloat(cuota.monto_a_pagar ?? NaN);
  if (!Number.isNaN(montoAPagar)) {
    return Math.max(0, Math.round(montoAPagar * 100) / 100);
  }

  const saldoBackend = parseFloat(cuota.saldo_pendiente ?? NaN);
  if (!Number.isNaN(saldoBackend)) {
    return Math.max(0, Math.round(saldoBackend * 100) / 100);
  }

  const montoCuota = numberOrZero(cuota.monto);
  const moraActual = numberOrZero(cuota.mora);
  const excedenteAnterior = numberOrZero(cuota.excedente_anterior);
  const montoTotalAPagar = montoCuota + moraActual - excedenteAnterior;

  return Math.max(0, Math.round(montoTotalAPagar * 100) / 100);
};

const calcularTotalPagado = (cuota) => {
  if (typeof cuota.monto_pagado === 'number') {
    return Math.round(cuota.monto_pagado * 100) / 100;
  }

  const pagosAplicados = Array.isArray(cuota.pagos) ? cuota.pagos : [];
  const totalPagado = pagosAplicados.reduce((acc, pago) => {
    const pagoACuota = parseFloat(pago.pago_aplicado_cuota ?? 0) || 0;
    const pagoAMora = parseFloat(pago.pago_aplicado_mora ?? 0) || 0;
    return acc + pagoACuota + pagoAMora;
  }, 0);

  return Math.round(totalPagado * 100) / 100;
};

const PrestamosList = ({
  selectedCliente,
  cuotasPendientes,
  isCuotaPagable,
  formatDate,
  handlePagarCuota,
  handleVerCaptura,
  handleCancelarPrestamo,
  handleVerComprobante,
  handleVerComprobantesMultiples
}) => {
  if (!selectedCliente) return null;

  if (cuotasPendientes.length === 0) {
    return (
      <div className="mb-6 rounded-2xl border border-amber-200 bg-amber-50 p-4">
        <p className="text-sm font-medium text-amber-900">
          EL CLIENTE SELECCIONADO NO TIENE PRESTAMOS ACTIVOS O CUOTAS PENDIENTES.
        </p>
      </div>
    );
  }

  return (
    <div className="mb-6">
      <h3 className="mb-3 text-sm font-semibold uppercase tracking-[0.16em] text-slate-500">
        PRESTAMOS Y CUOTAS PENDIENTES DE {selectedCliente.nombre} {selectedCliente.apellido}
      </h3>

      {cuotasPendientes.map((prestamo, index) => {
        const cuotasOrdenadas = ordenarCuotas(prestamo.cuotas);

        return (
          <div key={index} className="ui-card-soft mb-4 rounded-2xl p-3 sm:p-4">
            <div className="mb-3 flex flex-col items-start justify-between gap-3 md:flex-row md:items-center">
              <div>
                <h4 className="text-base font-semibold text-slate-900">
                  PRESTAMO #{prestamo.prestamo.idPrestamo} - {prestamo.prestamo.modalidad.toUpperCase()}
                </h4>
                <p className="text-sm text-slate-600">
                  MONTO: {formatCurrency(prestamo.prestamo.monto)} - FRECUENCIA: {prestamo.prestamo.frecuencia.toUpperCase()} - CUOTAS: {prestamo.prestamo.cuotas} - ABONADO POR: {prestamo.prestamo.abonado_por || 'N/A'}
                </p>
              </div>
              <button
                onClick={() => handleCancelarPrestamo(prestamo)}
                className="rounded-lg bg-amber-600 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-amber-700"
              >
                CANCELAR TOTAL DE PRESTAMO
              </button>
            </div>

            <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
              <table className="min-w-[1120px] table-fixed text-sm">
                <colgroup>
                  <col className="w-[8%]" />
                  <col className="w-[15%]" />
                  <col className="w-[11%]" />
                  <col className="w-[14%]" />
                  <col className="w-[12%]" />
                  <col className="w-[10%]" />
                  <col className="w-[9%]" />
                  <col className="w-[8%]" />
                  <col className="w-[13%]" />
                </colgroup>
                <thead>
                  <tr className="border-b border-slate-200 text-center text-xs font-semibold uppercase tracking-[0.1em] text-slate-500">
                    <th className="py-2 px-3 text-center">CUOTA</th>
                    <th className="py-2 px-3 text-center">FECHA VENCIMIENTO</th>
                    <th className="py-2 px-3 text-center">MONTO</th>
                    <th className="py-2 px-3 text-left">MONTO A PAGAR</th>
                    <th className="py-2 px-3 text-center">EXCEDENTE ANTERIOR</th>
                    <th className="py-2 px-3 text-center">ESTADO</th>
                    <th className="py-2 px-3">DIAS MORA</th>
                    <th className="py-2 px-3">MORA</th>
                    <th className="py-2 px-3 text-left">ACCION</th>
                  </tr>
                </thead>
                <tbody>
                  {cuotasOrdenadas.map((cuota, rowIndex) => {
                    const pagosAplicados = Array.isArray(cuota.pagos) ? cuota.pagos : [];
                    const totalPagado = calcularTotalPagado(cuota);
                    const excedenteAnterior = numberOrZero(cuota.excedente_anterior);
                    const saldoPendiente = calcularSaldoPendiente(cuota);
                    const { label: estadoLabel, className: estadoClass } = getEstadoBadge(cuota.estado);

                    const mostrarSeparador =
                      rowIndex > 0 &&
                      esCuotaConvenio(cuota) &&
                      !esCuotaConvenio(cuotasOrdenadas[rowIndex - 1]);

                    const renderComprobanteButton = () => (
                      <button
                        onClick={() => {
                          const tienePagosMultiples = pagosAplicados.length > 1;
                          if (tienePagosMultiples) {
                            handleVerComprobantesMultiples(prestamo.prestamo, cuota, pagosAplicados);
                          } else {
                            handleVerComprobante(prestamo.prestamo, cuota);
                          }
                        }}
                        className="rounded-md bg-red-700 px-2 py-1 text-xs font-semibold text-white transition hover:bg-red-800"
                      >
                        VER COMPROBANTE{pagosAplicados.length > 1 ? 'S' : ''}
                        {pagosAplicados.length > 1 && (
                          <span className="ml-1 bg-white text-red-700 rounded-full px-1.5 py-0.5 text-xs font-bold">
                            {pagosAplicados.length}
                          </span>
                        )}
                      </button>
                    );

                    return (
                      <React.Fragment key={cuota.idCuota}>
                        {mostrarSeparador && (
                          <tr className="border-y-2 border-blue-300 bg-blue-50">
                            <td colSpan="9" className="px-3 py-2 text-center text-xs font-bold uppercase tracking-[0.14em] text-blue-800">
                              CUOTAS DEL CONVENIO
                            </td>
                          </tr>
                        )}
                        <tr className="border-b border-slate-100 text-slate-700 last:border-b-0 hover:bg-slate-50">
                          <td className="px-3 py-2 text-center">{cuota.numero_cuota}</td>
                          <td className="px-3 py-2 text-center">{formatDate(cuota.fecha_vencimiento)}</td>
                          <td className="px-3 py-2 text-center whitespace-nowrap font-semibold">{formatCurrency(cuota.monto)}</td>
                          <td className="px-3 py-2 text-left">
                            <div className="font-semibold whitespace-nowrap">{formatCurrency(saldoPendiente)}</div>
                            <div className="text-xs text-slate-500 whitespace-nowrap">Pagado: {formatCurrency(totalPagado)}</div>
                          </td>
                          <td className="px-3 py-2 text-center whitespace-nowrap">{formatCurrency(excedenteAnterior)}</td>
                          <td className="px-3 py-2 text-center">
                            <span className={`inline-block py-1 px-2 rounded-full text-xs font-medium ${estadoClass}`}>
                              {estadoLabel}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-center">{cuota.dias_mora}</td>
                          <td className="px-3 py-2 text-center whitespace-nowrap">{formatCurrency(cuota.mora)}</td>
                          <td className="px-3 py-2 align-top text-left">
                            <div className="flex flex-wrap gap-2">
                            {esCuotaPagada(cuota) ? (
                              <>
                                {(cuota.modalidad === 'virtual' ||
                                  (cuota.estado === 'prepagado' && !cuota.modalidad)) && (
                                  <button
                                    onClick={() => handleVerCaptura(prestamo.prestamo, cuota)}
                                    className="rounded-md bg-blue-600 px-2 py-1 text-xs font-semibold text-white transition hover:bg-blue-700"
                                  >
                                    VER CAPTURA
                                  </button>
                                )}
                                {cuota.estado === 'pagado' && renderComprobanteButton()}
                              </>
                            ) : (
                              <>
                                <button
                                  onClick={() => handlePagarCuota(prestamo.prestamo, cuota)}
                                  className={`rounded-md px-2 py-1 text-xs font-semibold ${
                                    isCuotaPagable(prestamo, cuota)
                                      ? 'bg-green-600 text-white transition hover:bg-green-700'
                                      : 'bg-gray-400 text-white cursor-not-allowed'
                                  }`}
                                  disabled={!isCuotaPagable(prestamo, cuota)}
                                >
                                  PAGAR
                                </button>
                                {pagosAplicados.length > 0 && renderComprobanteButton()}
                              </>
                            )}
                            </div>
                          </td>
                        </tr>
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default PrestamosList;
